/**
 * MifeLife v2 — Contact (/iletisim). TR / EN / DE.
 * General / mentor / partnership inquiries + contact form + socials.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Handshake, UserPlus, Clock, Loader2, CheckCircle2, Linkedin, Twitter, Instagram } from 'lucide-react';
import { InfoShell, CONTACT_EMAIL, SOCIALS } from '../../components/v2/V2InfoPrimitives';
import { SEO } from '../../components/SEO';
import { apiRequest } from '../../lib/utils';

const COPY = {
  tr: {
    metaTitle: 'İletişim — MifeLife | Bize Ulaşın',
    metaDesc: 'MifeLife ile iletişime geçin. Genel sorular, mentör başvuruları ve iş birliği talepleri için iletişim formu. En geç 48 saat içinde dönüş yapıyoruz.',
    eyebrow: 'İLETİŞİM',
    h1: 'Bize ulaşın',
    lead: 'Sorularını, başvurularını veya iş birliği tekliflerini bekliyoruz. Ekibimiz en geç 48 saat içinde dönüş yapar.',
    cards: [
      { icon: MessageSquare, t: 'Genel sorular', d: 'Platform, hesap ve kullanım hakkında her şey.' },
      { icon: UserPlus, t: 'Mentör başvuruları', d: 'Mentör olmak için başvuru ve süreç soruları.' },
      { icon: Handshake, t: 'İş birlikleri', d: 'Kurumsal, B2B ve partnerlik teklifleri.' },
    ],
    responseTitle: 'Yanıt süresi',
    response: 'Tüm mesajlara hafta içi en geç 48 saat içinde yanıt veriyoruz.',
    emailLabel: 'E-posta',
    socialLabel: 'Sosyal medya',
    formTitle: 'Mesaj gönder',
    fName: 'Ad Soyad', fEmail: 'E-posta', fCategory: 'Konu', fSubject: 'Başlık (opsiyonel)', fMessage: 'Mesajın',
    cats: [{ v: 'general', l: 'Genel soru' }, { v: 'mentor', l: 'Mentör başvurusu' }, { v: 'partnership', l: 'İş birliği' }, { v: 'support', l: 'Destek' }],
    send: 'Gönder',
    okTitle: 'Mesajın alındı 🎉',
    okSub: 'En geç 48 saat içinde dönüş yapacağız. Teşekkürler!',
    again: 'Yeni mesaj gönder',
    errReq: 'Lütfen ad, e-posta ve en az 10 karakterlik mesaj gir.',
  },
  en: {
    metaTitle: 'Contact — MifeLife | Get in Touch',
    metaDesc: 'Contact MifeLife. A form for general questions, mentor applications and partnership requests. We respond within 48 hours.',
    eyebrow: 'CONTACT',
    h1: 'Get in touch',
    lead: 'We’d love to hear your questions, applications or partnership proposals. Our team replies within 48 hours.',
    cards: [
      { icon: MessageSquare, t: 'General questions', d: 'Anything about the platform, account and usage.' },
      { icon: UserPlus, t: 'Mentor applications', d: 'Questions about applying and the mentor process.' },
      { icon: Handshake, t: 'Partnerships', d: 'Enterprise, B2B and partnership proposals.' },
    ],
    responseTitle: 'Response time',
    response: 'We answer all messages within 48 hours on business days.',
    emailLabel: 'Email',
    socialLabel: 'Social media',
    formTitle: 'Send a message',
    fName: 'Full name', fEmail: 'Email', fCategory: 'Topic', fSubject: 'Subject (optional)', fMessage: 'Your message',
    cats: [{ v: 'general', l: 'General question' }, { v: 'mentor', l: 'Mentor application' }, { v: 'partnership', l: 'Partnership' }, { v: 'support', l: 'Support' }],
    send: 'Send',
    okTitle: 'Message received 🎉',
    okSub: 'We’ll get back to you within 48 hours. Thank you!',
    again: 'Send another message',
    errReq: 'Please enter your name, email and a message of at least 10 characters.',
  },
  de: {
    metaTitle: 'Kontakt — MifeLife | Kontaktieren Sie uns',
    metaDesc: 'Kontaktieren Sie MifeLife. Ein Formular für allgemeine Fragen, Mentor-Bewerbungen und Partnerschaftsanfragen. Wir antworten innerhalb von 48 Stunden.',
    eyebrow: 'KONTAKT',
    h1: 'Kontaktieren Sie uns',
    lead: 'Wir freuen uns über Ihre Fragen, Bewerbungen oder Partnerschaftsvorschläge. Unser Team antwortet innerhalb von 48 Stunden.',
    cards: [
      { icon: MessageSquare, t: 'Allgemeine Fragen', d: 'Alles rund um Plattform, Konto und Nutzung.' },
      { icon: UserPlus, t: 'Mentor-Bewerbungen', d: 'Fragen zur Bewerbung und zum Mentor-Prozess.' },
      { icon: Handshake, t: 'Partnerschaften', d: 'Enterprise-, B2B- und Partnerschaftsvorschläge.' },
    ],
    responseTitle: 'Antwortzeit',
    response: 'Wir beantworten alle Nachrichten an Werktagen innerhalb von 48 Stunden.',
    emailLabel: 'E-Mail',
    socialLabel: 'Soziale Medien',
    formTitle: 'Nachricht senden',
    fName: 'Vollständiger Name', fEmail: 'E-Mail', fCategory: 'Thema', fSubject: 'Betreff (optional)', fMessage: 'Ihre Nachricht',
    cats: [{ v: 'general', l: 'Allgemeine Frage' }, { v: 'mentor', l: 'Mentor-Bewerbung' }, { v: 'partnership', l: 'Partnerschaft' }, { v: 'support', l: 'Support' }],
    send: 'Senden',
    okTitle: 'Nachricht erhalten 🎉',
    okSub: 'Wir melden uns innerhalb von 48 Stunden. Vielen Dank!',
    again: 'Weitere Nachricht senden',
    errReq: 'Bitte geben Sie Name, E-Mail und eine Nachricht mit mindestens 10 Zeichen ein.',
  },
};

const SOCIAL_ICON = { linkedin: Linkedin, x: Twitter, instagram: Instagram };

export default function V2ContactPage() {
  const [lang, setLang] = useState('tr');
  const T = COPY[lang] || COPY.tr;
  const [f, setF] = useState({ name: '', email: '', category: 'general', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!f.name.trim() || !f.email.trim() || f.message.trim().length < 10) { setErr(T.errReq); return; }
    setBusy(true);
    try {
      await apiRequest('/v2/contact', { method: 'POST', body: JSON.stringify({ ...f, subject: f.subject || null }) });
      setDone(true);
    } catch (e2) {
      setErr(e2?.message || T.errReq);
    } finally { setBusy(false); }
  };

  return (
    <InfoShell lang={lang} onLang={setLang} testid="v2-contact-page">
      <SEO title={T.metaTitle} description={T.metaDesc} path="/iletisim" />
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{T.eyebrow}</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">{T.h1}</h1>
        <p className="text-base text-slate-400 leading-relaxed max-w-2xl mb-12">{T.lead}</p>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
          {/* Left — info */}
          <div className="space-y-4">
            {T.cards.map((c, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex gap-4" data-testid={`v2-contact-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 text-violet-300 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-base font-semibold mb-1">{c.t}</h3>
                  <p className="text-[13px] text-slate-400 leading-relaxed">{c.d}</p>
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2"><Clock className="w-3.5 h-3.5" /> {T.responseTitle}</div>
              <p className="text-[13px] text-slate-300">{T.response}</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{T.emailLabel}</div>
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 text-sm text-violet-300 hover:text-violet-200 transition" data-testid="v2-contact-email"><Mail className="w-4 h-4" /> {CONTACT_EMAIL}</a>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2 mt-5">{T.socialLabel}</div>
              <div className="flex gap-2">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICON[s.key] || Mail;
                  return <a key={s.key} href={s.href} aria-label={s.label} className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-center text-slate-400 hover:text-white hover:border-white/20 transition" data-testid={`v2-contact-social-${s.key}`}><Icon className="w-4 h-4" /></a>;
                })}
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="v2-contact-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">{T.okTitle}</h2>
                <p className="text-sm text-slate-400 mb-6">{T.okSub}</p>
                <button onClick={() => { setDone(false); setF({ name: '', email: '', category: 'general', subject: '', message: '' }); }} className="text-sm text-violet-300 hover:text-violet-200 transition" data-testid="v2-contact-again">{T.again}</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="v2-contact-form">
                <h2 className="text-lg font-semibold tracking-tight mb-1">{T.formTitle}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label={T.fName}><input required value={f.name} onChange={(e) => set('name', e.target.value)} className={INP} data-testid="v2-contact-name" /></Field>
                  <Field label={T.fEmail}><input required type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className={INP} data-testid="v2-contact-email-input" /></Field>
                </div>
                <Field label={T.fCategory}>
                  <select value={f.category} onChange={(e) => set('category', e.target.value)} className={INP} data-testid="v2-contact-category">
                    {T.cats.map((c) => <option key={c.v} value={c.v} className="bg-[#0a0a14]">{c.l}</option>)}
                  </select>
                </Field>
                <Field label={T.fSubject}><input value={f.subject} onChange={(e) => set('subject', e.target.value)} className={INP} data-testid="v2-contact-subject" /></Field>
                <Field label={T.fMessage}><textarea required rows={5} minLength={10} maxLength={4000} value={f.message} onChange={(e) => set('message', e.target.value)} className={`${INP} h-auto py-2.5 resize-none`} data-testid="v2-contact-message" /></Field>
                {err && <div className="text-sm text-rose-300" data-testid="v2-contact-error">{err}</div>}
                <button type="submit" disabled={busy} className="w-full h-12 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-contact-submit">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} {T.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </InfoShell>
  );
}

const INP = 'w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition';
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
    {children}
  </label>
);
